import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  Landmark,
  TrendingUp,
  Wallet,
  AlertTriangle,
  Building2,
  Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Cite,
  SourcesList,
  SectionHeader,
  IconList,
  ChartCard,
  tooltipStyle,
  type Source,
} from "./article-kit";

const bankingComparison = [
  { label: "Mobile, AL", unbanked: 8, underbanked: 17 },
  { label: "Alabama", unbanked: 7.5, underbanked: 16 },
  { label: "United States", unbanked: 4.5, underbanked: 14.1 },
];

const sources: Source[] = [
  {
    n: 1,
    label:
      "City of Mobile — Financial Empowerment Action Plan (source of the 8% unbanked / 17% underbanked / 167% Bank On growth figures)",
    url: "https://www.cityofmobile.org/departments/innovation-performance/financial-empowerment/",
  },
  {
    n: 2,
    label:
      "FDIC — 2021 National Survey of Unbanked and Underbanked Households (national + state comparison)",
    url: "https://www.fdic.gov/analysis/household-survey/index.html",
  },
  {
    n: 3,
    label: "Bank On — National Data Hub (Bank On account standards and impact)",
    url: "https://joinbankon.org/about/national-data-hub/",
  },
  {
    n: 4,
    label: "Cities for Financial Empowerment Fund — Bank On program overview",
    url: "https://cfefund.org/program/bank-on/",
  },
  {
    n: 5,
    label:
      "Federal Reserve — Report on the Economic Well-Being of U.S. Households (2024)",
    url: "https://www.federalreserve.gov/publications/report-economic-well-being-us-households.htm",
  },
  {
    n: 6,
    label: "U.S. Census Bureau — QuickFacts: Mobile city, Alabama",
    url: "https://www.census.gov/quickfacts/fact/table/mobilecityalabama/PST045224",
  },
  {
    n: 7,
    label:
      "U.S. Minority Business Development Agency — Alabama State of Minority Business Fact Sheet (2022)",
    url: "https://www.mbda.gov/sites/default/files/media/document/2022/Alabama%20Fact%20Sheet.pdf",
  },
  {
    n: 8,
    label:
      "UA Center for Business & Economic Research (CBER) — Alabama small-business baselines",
    url: "https://cber.culverhouse.ua.edu/",
  },
  {
    n: 9,
    label: "CDFI Fund — List of certified Community Development Financial Institutions",
    url: "https://www.cdfifund.gov/programs-training/certification/cdfi",
  },
];

const UnbankedUnderbankedArticle = () => {
  return (
    <div className="space-y-12">
      {/* Hero stat */}
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="rounded-2xl border border-border bg-card/40 p-6 md:p-8"
      >
        <div className="flex items-center gap-2 mb-4">
          <Wallet className="h-4 w-4 text-primary" />
          <span className="text-xs font-semibold tracking-[0.18em] uppercase text-primary">
            Mobile, Alabama — Banking Access
          </span>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <div className="text-5xl md:text-6xl font-display font-bold text-foreground">
              8%<Cite ns={[1]} />
            </div>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Share of Mobile households that are <strong>unbanked</strong> — no checking or
              savings account at an insured institution. Roughly double the U.S. rate of ~4.5%.<Cite
                ns={[2]}
              />
            </p>
          </div>
          <div>
            <div className="text-5xl md:text-6xl font-display font-bold text-foreground">
              17%<Cite ns={[1]} />
            </div>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Mobile households that are <strong>underbanked</strong> — they have an account, but
              still rely on costly non-bank services (check cashers, payday lenders, money
              orders).
            </p>
          </div>
        </div>
      </motion.section>

      {/* Lead */}
      <section>
        <p className="text-lg text-muted-foreground leading-relaxed">
          The City of Mobile's Financial Empowerment Action Plan opens with a simple finding:
          one in four Mobile households is either disconnected from the mainstream banking
          system or only loosely tethered to it.<Cite ns={[1]} /> That isn't a consumer-finance
          footnote — it is a direct constraint on small-business credit, payroll, contracting,
          and the wealth-building loop the city's other economic mobility programs depend on.
        </p>
      </section>

      {/* Chart */}
      <section>
        <SectionHeader
          title="Mobile vs. Alabama vs. the U.S."
          description="Mobile's unbanked share runs well above the national figure, and its underbanked share sits noticeably above national too. Alabama numbers shown for context."
        />
        <ChartCard
          title="Unbanked & underbanked household shares"
          subtitle="Mobile figures from the City's Financial Empowerment Action Plan; AL and U.S. figures from the FDIC 2021 National Survey"
          sources={[1, 2]}
          footnote="Alabama state-level figures are approximate, drawn from the FDIC 2021 state appendix; the FDIC publishes updated household-survey waves periodically."
          height={280}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={bankingComparison}
              margin={{ top: 12, right: 12, left: 0, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="label"
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                tickMargin={6}
              />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value: number, name: string) => [`${value}%`, name]}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar
                dataKey="unbanked"
                name="Unbanked households"
                fill="hsl(var(--primary))"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="underbanked"
                name="Underbanked households"
                fill="hsl(var(--primary) / 0.5)"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      {/* Definitions */}
      <section>
        <SectionHeader
          title="What these two words actually mean"
          description="The FDIC's National Survey is the standard definition. The Mobile plan uses the same vocabulary."
        />
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-border p-5">
            <Badge variant="outline" className="text-[10px] mb-2">
              Unbanked
            </Badge>
            <p className="text-sm text-muted-foreground leading-relaxed">
              No one in the household has a checking or savings account at a bank or credit
              union. Cash and prepaid cards do all the work — including, often, running a
              small business.<Cite ns={[2]} />
            </p>
          </div>
          <div className="rounded-xl border border-border p-5">
            <Badge variant="outline" className="text-[10px] mb-2">
              Underbanked
            </Badge>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The household has an account, but still relied on a nonbank money order, check
              cashing, payday loan, pawn loan, refund anticipation loan, or rent-to-own service
              in the past year.<Cite ns={[2]} />
            </p>
          </div>
        </div>
      </section>

      {/* Supporting stats */}
      <section>
        <SectionHeader
          kicker="Supporting figures"
          title="The numbers behind the gap"
        />
        <div className="grid md:grid-cols-3 gap-4">
          <div className="rounded-xl bg-primary/5 border border-primary/20 p-5">
            <TrendingUp className="h-5 w-5 text-primary mb-2" />
            <div className="text-2xl font-bold text-foreground">+167%</div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Increase in new Bank On–certified accounts opened in Mobile between 2020 and
              2021.<Cite ns={[1]} />
            </p>
          </div>
          <div className="rounded-xl bg-primary/5 border border-primary/20 p-5">
            <AlertTriangle className="h-5 w-5 text-primary mb-2" />
            <div className="text-2xl font-bold text-foreground">~4.5%</div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              U.S. unbanked rate in the FDIC's most recent National Survey — Mobile sits at
              roughly double that figure.<Cite ns={[2]} />
            </p>
          </div>
          <div className="rounded-xl bg-primary/5 border border-primary/20 p-5">
            <Users className="h-5 w-5 text-primary mb-2" />
            <div className="text-2xl font-bold text-foreground">~1 in 4</div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Mobile households that are unbanked <em>or</em> underbanked — combining the City's
              two headline figures.<Cite ns={[1]} />
            </p>
          </div>
        </div>
      </section>

      {/* Why */}
      <section>
        <SectionHeader title="Why the gap persists" />
        <IconList
          variant="arrow"
          items={[
            {
              lead: "Branch deserts.",
              body: (
                <>
                  Bank branches have thinned in north and west Mobile, concentrating in higher-
                  income corridors. Distance and fee structure both raise the cost of being
                  banked for households in priority ZIPs.<Cite ns={[1]} />
                </>
              ),
            },
            {
              lead: "Fee and overdraft history.",
              body: (
                <>
                  ChexSystems flags and prior overdrafts disqualify many households from
                  opening standard accounts — the exact problem Bank On certification was
                  designed to solve by capping fees and removing overdraft penalties.<Cite
                    ns={[3, 4]}
                  />
                </>
              ),
            },
            {
              lead: "Cash-economy informality.",
              body: (
                <>
                  Sole proprietors and contractors paid in cash leave no underwriting trail.
                  Without deposit history, even a profitable micro-business can't access a line
                  of credit, a vehicle loan, or a working-capital product.<Cite ns={[5]} />
                </>
              ),
            },
            {
              lead: "Distrust + digital-only gaps.",
              body: (
                <>
                  Neobanks and digital-only accounts solve some of the fee problem but rarely
                  the identity-verification and ChexSystems barriers — so the unbanked share
                  doesn't fall as fast as account-product innovation suggests.<Cite ns={[5]} />
                </>
              ),
            },
          ]}
        />
      </section>

      {/* What Mobile is doing */}
      <section>
        <SectionHeader title="What Mobile is already doing" />
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-border p-5">
            <Landmark className="h-5 w-5 text-primary mb-2" />
            <h3 className="font-display font-semibold text-foreground mb-1">
              Bank On Mobile coalition
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The City's Bank On coalition pairs participating banks and credit unions with
              certified low-fee, no-overdraft account products. The plan reports a 167%
              increase in new certified accounts between 2020 and 2021.<Cite ns={[1, 3]} />
            </p>
          </div>
          <div className="rounded-xl border border-border p-5">
            <Building2 className="h-5 w-5 text-primary mb-2" />
            <h3 className="font-display font-semibold text-foreground mb-1">
              Financial Empowerment Action Plan
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Mobile's plan formalizes the 8% / 17% baseline and assigns programmatic owners
              for outreach, financial counseling, and account-product partnerships.<Cite
                ns={[1]}
              />
            </p>
          </div>
          <div className="rounded-xl border border-border p-5">
            <Users className="h-5 w-5 text-primary mb-2" />
            <h3 className="font-display font-semibold text-foreground mb-1">
              Cities for Financial Empowerment Fund
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The national CFE Fund supplies the Bank On account standards Mobile's bank
              partners certify against — the same standards used in dozens of peer cities.<Cite
                ns={[4]}
              />
            </p>
          </div>
          <div className="rounded-xl border border-border p-5">
            <Wallet className="h-5 w-5 text-primary mb-2" />
            <h3 className="font-display font-semibold text-foreground mb-1">
              MBDA & UA CBER ecosystem
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              MBDA's Alabama fact sheet and UA's Center for Business & Economic Research
              supply the small-business baselines Mobile operators can cite when applying for
              capital and technical assistance.<Cite ns={[7, 8]} />
            </p>
          </div>
        </div>
      </section>

      {/* What businesses can do */}
      <section>
        <SectionHeader title="What small businesses can do this quarter" />
        <IconList
          variant="check"
          items={[
            {
              lead: "Open a Bank On–certified business account.",
              body: (
                <>
                  No-overdraft, capped-fee account products are available locally through Bank
                  On Mobile partners — pick one and migrate operating cash off prepaid cards.<Cite
                    ns={[3]}
                  />
                </>
              ),
            },
            {
              lead: "Separate personal and business finances.",
              body: (
                <>
                  A dedicated business deposit account is the cheapest way to start building
                  the underwriting history lenders and grant reviewers actually look at.
                </>
              ),
            },
            {
              lead: "Enroll employees in direct deposit + Bank On.",
              body: (
                <>
                  Payroll-driven account openings are one of the most effective ways to move
                  unbanked workers into the mainstream system — and reduce employer pay-card
                  fees in the process.<Cite ns={[4]} />
                </>
              ),
            },
            {
              lead: "Partner with an Alabama-certified CDFI.",
              body: (
                <>
                  CDFIs underwrite small-balance working capital that traditional banks won't
                  touch — the natural counterpart to a newly banked operating account.<Cite
                    ns={[9]}
                  />
                </>
              ),
            },
          ]}
        />
      </section>

      {/* Closing */}
      <section>
        <SectionHeader title="The bottom line" />
        <p className="text-muted-foreground leading-relaxed">
          Mobile's 8% unbanked / 17% underbanked figure is not just a household-finance
          statistic. It is the input layer for everything the City is trying to do on
          contracting, wealth building, and DBE participation. The infrastructure to fix it —
          Bank On Mobile, the Financial Empowerment Action Plan, CDFI partners, MBDA — already
          exists. The lift is moving households and small businesses onto it, one operating
          account at a time.
        </p>
      </section>

      <SourcesList sources={sources} />
    </div>
  );
};

export default UnbankedUnderbankedArticle;
export const UNBANKED_SLUG = "mobile-unbanked-underbanked-banking-access-gap";